// "Esqueci minha senha" e a redefinição pelo link do e-mail.
//
// A resposta do pedido é sempre a mesma, exista ou não a conta: a tela não pode
// servir para descobrir quais e-mails estão cadastrados.

import { useState } from 'react'
import { Alert, Button, Form, Input, Result } from 'antd'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { passwordSchema } from '@c1hd/shared'
import { ApiError, api } from '../lib/api'
import styles from './Login.module.css'

function Shell({
  title,
  subtitle,
  children,
}: {
  title: string
  subtitle?: string
  children: React.ReactNode
}) {
  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div>
          <h1 className={styles.title}>{title}</h1>
          {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
        </div>
        {children}
      </div>
    </div>
  )
}

export function ForgotPasswordPage() {
  const [sent, setSent] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (sent) {
    return (
      <Shell title="Confira o seu e-mail">
        <Result
          status="success"
          title="Pedido enviado"
          subTitle="Se existir uma conta com esse e-mail, o link para criar uma nova senha chega em instantes. Ele vale por 30 minutos."
          extra={<Link to="/login">Voltar para o login</Link>}
        />
      </Shell>
    )
  }

  return (
    <Shell
      title="Esqueci minha senha"
      subtitle="Informe o e-mail da sua conta e enviamos um link para criar uma nova senha."
    >
      {error && <Alert type="error" message={error} showIcon />}
      <Form
        layout="vertical"
        requiredMark={false}
        onFinish={async (values: { email: string }) => {
          setSubmitting(true)
          setError(null)
          try {
            await api.post('/auth/forgot-password', values)
            setSent(true)
          } catch (err) {
            setError(err instanceof ApiError ? err.message : 'Não foi possível enviar')
          } finally {
            setSubmitting(false)
          }
        }}
      >
        <Form.Item
          name="email"
          label="E-mail"
          rules={[{ required: true, type: 'email', message: 'Informe um e-mail válido' }]}
        >
          <Input autoComplete="username" placeholder="voce@suaempresa.com.br" />
        </Form.Item>
        <Form.Item style={{ marginBottom: 8 }}>
          <Button type="primary" htmlType="submit" block loading={submitting}>
            Enviar link
          </Button>
        </Form.Item>
      </Form>
      <Link className={styles.link} to="/login">
        Voltar para o login
      </Link>
    </Shell>
  )
}

export function ResetPasswordPage() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const token = params.get('token') ?? ''
  const [form] = Form.useForm<{ password: string; confirm: string }>()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!token) {
    return (
      <Shell title="Link inválido">
        <Alert
          type="error"
          showIcon
          message="Este link está incompleto"
          description="Abra o link direto do e-mail que você recebeu, ou peça um novo."
        />
        <Link className={styles.link} to="/esqueci-minha-senha">
          Pedir um novo link
        </Link>
      </Shell>
    )
  }

  return (
    <Shell
      title="Criar nova senha"
      subtitle="Escolha a senha que vai usar daqui em diante."
    >
      {error && <Alert type="error" message={error} showIcon />}
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={async (values) => {
          setSubmitting(true)
          setError(null)
          try {
            await api.post('/auth/reset-password', { token, password: values.password })
            navigate('/login', { replace: true })
          } catch (err) {
            setError(err instanceof ApiError ? err.message : 'Não foi possível redefinir')
          } finally {
            setSubmitting(false)
          }
        }}
      >
        <Form.Item
          name="password"
          label="Nova senha"
          rules={[
            { required: true, message: 'Informe a nova senha' },
            {
              validator: (_, value: string) => {
                const parsed = passwordSchema.safeParse(value ?? '')
                return parsed.success
                  ? Promise.resolve()
                  : Promise.reject(new Error(parsed.error.issues[0]?.message))
              },
            },
          ]}
        >
          <Input.Password autoComplete="new-password" />
        </Form.Item>
        <Form.Item
          name="confirm"
          label="Repita a nova senha"
          dependencies={['password']}
          rules={[
            { required: true, message: 'Repita a nova senha' },
            {
              validator: (_, value: string) =>
                !value || value === form.getFieldValue('password')
                  ? Promise.resolve()
                  : Promise.reject(new Error('As senhas não conferem')),
            },
          ]}
        >
          <Input.Password autoComplete="new-password" />
        </Form.Item>
        <Form.Item style={{ marginBottom: 0 }}>
          <Button type="primary" htmlType="submit" block loading={submitting}>
            Salvar e entrar
          </Button>
        </Form.Item>
      </Form>
    </Shell>
  )
}
